import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useCurrentOrganization } from '@/hooks/use-organization';
import { useProfile } from '@/hooks/useProfile';
import { api } from '@/lib/api';
import type { BillingGateStatusResponse } from '@codemerlin/shared/billing/types';
import { AlertTriangle, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { capFreeTierUsedForDisplay } from '@codemerlin/shared';
import { analytics } from '@/lib/analytics';

export function BillingSetupBanner() {
  const { t } = useTranslation('settings');
  const { organization } = useCurrentOrganization();
  const orgId = organization?.organizationId;
  const dismissKey = orgId ? `codemerlin:billing-banner-dismissed:${orgId}` : '';
  const { data: profile } = useProfile();
  const isAdmin = profile?.role === 'admin' || profile?.role === 'owner';
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!dismissKey) {
      setDismissed(false);
      return;
    }
    setDismissed(sessionStorage.getItem(dismissKey) === '1');
  }, [dismissKey, orgId]);

  const { data: gate, isLoading } = useQuery<BillingGateStatusResponse>({
    queryKey: ['billing', 'gate-status', orgId],
    queryFn: () => api.get<BillingGateStatusResponse>(`billing/gate-status/${orgId}`),
    enabled: Boolean(orgId && isAdmin),
    staleTime: 60_000,
  });

  const setupMutation = useMutation({
    mutationFn: () => api.post<{ url: string }>('billing/setup-session'),
    onSuccess: (data) => {
      analytics.capture('billing_setup_started', { organization_id: orgId });
      if (data.url) window.location.href = data.url;
    },
  });

  const shouldShow = useMemo(() => {
    if (!gate || !isAdmin || dismissed) return false;
    if (gate.blockedReason) return true;
    if (gate.freeReviewsRemaining !== null && gate.freeReviewsRemaining <= 2) return true;
    return false;
  }, [gate, isAdmin, dismissed]);

  // Fire billing_banner_shown once per visible mount.
  const shownRef = useRef(false);
  useEffect(() => {
    if (shouldShow && gate && orgId && !shownRef.current) {
      shownRef.current = true;
      analytics.capture('billing_banner_shown', {
        organization_id: orgId,
        reason: gate.blockedReason ?? 'low_free_reviews',
        free_reviews_remaining: gate.freeReviewsRemaining,
      });
    }
    if (!shouldShow) shownRef.current = false;
  }, [shouldShow, gate, orgId]);

  const dismiss = useCallback(() => {
    analytics.capture('billing_banner_dismissed', { organization_id: orgId });
    if (dismissKey) sessionStorage.setItem(dismissKey, '1');
    setDismissed(true);
  }, [dismissKey, orgId]);

  if (!orgId || !isAdmin || isLoading || !shouldShow || !gate) {
    return null;
  }

  const title =
    gate.blockedReason === 'PAYMENT_METHOD_REQUIRED'
      ? t('billing.banner.payment_failed_title', 'Payment issue')
      : gate.blockedReason === 'BILLING_SETUP_REQUIRED'
        ? t('billing.banner.setup_required_title', 'Billing setup required')
        : t('billing.banner.low_free_reviews_title', 'Free reviews running low');

  const description =
    gate.blockedReason === 'PAYMENT_METHOD_REQUIRED'
      ? t(
          'billing.banner.payment_failed_body',
          'We could not charge your saved payment method. Update billing to continue reviewing.'
        )
      : gate.blockedReason === 'BILLING_SETUP_REQUIRED'
        ? t(
            'billing.banner.setup_required_body',
            'You have used all free reviews. Set up billing to continue using CodeMerlin.'
          )
        : t('billing.banner.low_free_reviews_body', {
            defaultValue:
              'You have used {{used}} of {{limit}} free reviews. Set up billing to keep reviewing.',
            used: capFreeTierUsedForDisplay(gate.freeReviewsUsed, gate.freeReviewLimit),
            limit: gate.freeReviewLimit,
          });

  // When reviews are hard-blocked the admin must act; don't let them dismiss
  // the banner and lose the only UI that points to the fix.
  const canDismiss = !gate.blockedReason;

  return (
    <Alert className="mb-4 border-amber-500/50 bg-amber-50 dark:bg-amber-950/30">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="flex items-center justify-between gap-2 pr-8">
        {title}
        {canDismiss && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={dismiss}
            aria-label={t('billing.banner.dismiss', 'Dismiss')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </AlertTitle>
      <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span>{description}</span>
        <Button
          type="button"
          size="sm"
          disabled={setupMutation.isPending}
          onClick={() => {
            analytics.capture('billing_banner_cta_clicked', {
              organization_id: orgId,
              cta_type: gate.blockedReason ? 'manage_billing' : 'setup_billing',
            });
            setupMutation.mutate();
          }}
        >
          {setupMutation.isPending
            ? t('billing.banner.redirecting', 'Redirecting…')
            : t('billing.banner.cta', 'Set up billing')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
